import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { COMMAND_ATTRIBUTES, camelToReadable } from '@/config/commandAttributes';
import { X, Loader2, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { trackEvent } from '@/hooks/useAnalytics';

export default function SendCommandDialog({ device, devices, onClose }) {
  const { t } = useTranslation();

  // Determine mode: single device vs multi-device
  const isMulti = Array.isArray(devices) && devices.length > 0;
  const referenceDevice = isMulti ? devices[0] : device;
  const subtitle = isMulti
    ? t('bulk.sendingToDevices', { count: devices.length })
    : referenceDevice?.name;

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const [mode, setMode] = useState('saved');
  const [commandTypes, setCommandTypes] = useState([]);
  const [savedCommands, setSavedCommands] = useState([]);
  const [selectedSavedId, setSelectedSavedId] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [attributes, setAttributes] = useState({});
  const [textChannel, setTextChannel] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Multi-device progress state
  const [multiProgress, setMultiProgress] = useState(null); // { current, total, succeeded, failed }
  const [multiResult, setMultiResult] = useState(null); // { succeeded, failed, total, failedDevices }

  useEffect(() => {
    let cancelled = false;
    const refId = referenceDevice?.id;
    Promise.all([
      api.getCommandTypes(isMulti ? undefined : refId),
      isMulti ? Promise.resolve([]) : api.getCommands(refId),
    ])
      .then(([types, cmds]) => {
        if (cancelled) return;
        setCommandTypes(types || []);
        setSavedCommands(cmds || []);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [referenceDevice?.id, isMulti]);

  const handleTypeChange = (newType) => {
    setSelectedType(newType);
    setAttributes({});
  };

  const handleAttrChange = (key, value) => {
    setAttributes((prev) => ({ ...prev, [key]: value }));
  };

  const currentAttrs = COMMAND_ATTRIBUTES[selectedType] || [];

  const typeOptions = commandTypes.length > 0
    ? commandTypes.map((ct) => ct.type)
    : Object.keys(COMMAND_ATTRIBUTES);

  const canSend = (mode === 'saved' && !isMulti) ? !!selectedSavedId : !!selectedType;

  const buildPayload = (deviceId) => {
    if (mode === 'saved' && !isMulti) {
      return { id: Number(selectedSavedId), deviceId };
    }
    return {
      deviceId,
      type: selectedType,
      description: '',
      textChannel,
      attributes,
    };
  };

  const handleSend = async () => {
    setSending(true);
    setError(null);

    if (!isMulti) {
      // Single device mode (existing behavior)
      try {
        await api.sendCommand(buildPayload(device.id));
        trackEvent('device_command', {});
        onClose();
      } catch (err) {
        setError(err.message || t('settings.commandFailed'));
      } finally {
        setSending(false);
      }
      return;
    }

    // Multi-device mode
    let succeeded = 0;
    let failed = 0;
    const failedDevices = [];

    for (let i = 0; i < devices.length; i++) {
      const d = devices[i];
      setMultiProgress({ current: i + 1, total: devices.length, succeeded, failed });
      try {
        await api.sendCommand(buildPayload(d.id));
        succeeded++;
      } catch (err) {
        failed++;
        failedDevices.push({ id: d.id, name: d.name, error: err.message });
      }
    }

    setSending(false);
    setMultiResult({ succeeded, failed, total: devices.length, failedDevices });
    setMultiProgress(null);
  };

  const inputClass = 'w-full h-9 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/10';
  const selectClass = inputClass + ' bg-white dark:bg-slate-800';
  const labelClass = 'block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl dark:shadow-black/50 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-800">
          <div>
            <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200">{t('settings.sendCommand')}</h4>
            <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">{subtitle}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Multi-device result screen */}
        {multiResult ? (
          <div className="px-5 py-6 space-y-3">
            <div className="flex items-center gap-2">
              {multiResult.failed === 0 ? (
                <CheckCircle2 size={20} className="text-emerald-500" />
              ) : (
                <AlertCircle size={20} className="text-amber-500" />
              )}
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                {t('bulk.sentToDevices', { succeeded: multiResult.succeeded, total: multiResult.total })}
              </p>
            </div>
            {multiResult.failedDevices.length > 0 && (
              <div className="bg-red-50 dark:bg-red-500/10 rounded-xl p-3 space-y-1">
                {multiResult.failedDevices.map((fd) => (
                  <p key={fd.id} className="text-[11px] text-red-600">
                    {fd.name}: {fd.error}
                  </p>
                ))}
              </div>
            )}
            <button
              onClick={onClose}
              className="h-9 px-4 text-xs font-semibold rounded-xl text-white bg-blue-500 hover:bg-blue-600 transition-colors"
            >
              {t('settings.close', 'Close')}
            </button>
          </div>
        ) : (
          <>
            {/* Mode toggle — hide for multi when no saved commands */}
            {(!isMulti || savedCommands.length > 0) && (
              <div className="px-5 pt-4">
                <div className="flex gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-800">
                  {['saved', 'custom'].map((m) => (
                    <button
                      key={m}
                      onClick={() => { setMode(m); setError(null); }}
                      className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                        mode === m
                          ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 shadow-sm'
                          : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                      }`}
                    >
                      {m === 'saved' ? t('command.savedCommand', 'Saved Command') : t('command.customCommand', 'Custom Command')}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Body */}
            <div className="px-5 py-4 space-y-3">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={20} className="animate-spin text-slate-400" />
                </div>
              ) : mode === 'saved' && !isMulti ? (
                /* Saved mode — single device only */
                savedCommands.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-6">
                    {t('command.noSavedCommands', 'No saved commands available for this device')}
                  </p>
                ) : (
                  <div>
                    <label className={labelClass}>{t('settings.savedCommands')}</label>
                    <select
                      value={selectedSavedId}
                      onChange={(e) => setSelectedSavedId(e.target.value)}
                      className={selectClass}
                    >
                      <option value="">{t('settings.savedCommands')}...</option>
                      {savedCommands.map((cmd) => (
                        <option key={cmd.id} value={cmd.id}>
                          {cmd.description} ({t(`command.${cmd.type}`, camelToReadable(cmd.type))})
                        </option>
                      ))}
                    </select>
                  </div>
                )
              ) : (
                /* Custom mode (or multi-device default) */
                <>
                  <div>
                    <label className={labelClass}>{t('settings.commandType')}</label>
                    <select
                      value={selectedType}
                      onChange={(e) => handleTypeChange(e.target.value)}
                      className={selectClass}
                    >
                      <option value="">{t('settings.commandType')}...</option>
                      {typeOptions.map((ct) => (
                        <option key={ct} value={ct}>
                          {t(`command.${ct}`, camelToReadable(ct))}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="sendCmdTextChannel"
                      checked={textChannel}
                      onChange={(e) => setTextChannel(e.target.checked)}
                      className="rounded border-slate-300"
                    />
                    <label htmlFor="sendCmdTextChannel" className="text-xs text-slate-600 dark:text-slate-400">
                      {t('settings.sendViaSms')}
                    </label>
                  </div>

                  {currentAttrs.length > 0 && (
                    <div className="space-y-3">
                      {currentAttrs.map((attr) => (
                        <div key={attr.key}>
                          <label className={labelClass}>
                            {t(`command.${attr.label}`, camelToReadable(attr.label))}
                          </label>
                          {attr.type === 'boolean' ? (
                            <div className="flex items-center gap-2 h-9">
                              <input
                                type="checkbox"
                                checked={!!attributes[attr.key]}
                                onChange={(e) => handleAttrChange(attr.key, e.target.checked)}
                                className="rounded border-slate-300"
                              />
                              <span className="text-xs text-slate-500">{t('settings.enabled')}</span>
                            </div>
                          ) : attr.type === 'number' ? (
                            <input
                              type="number"
                              value={attributes[attr.key] ?? ''}
                              onChange={(e) => handleAttrChange(attr.key, e.target.value ? Number(e.target.value) : '')}
                              className={inputClass}
                            />
                          ) : (
                            <input
                              type="text"
                              value={attributes[attr.key] ?? ''}
                              onChange={(e) => handleAttrChange(attr.key, e.target.value)}
                              className={inputClass}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Multi-device progress */}
              {multiProgress && (
                <div className="space-y-1.5">
                  <p className="text-xs text-slate-500">
                    {t('bulk.updating', { completed: multiProgress.current, total: multiProgress.total })}
                  </p>
                  <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all duration-200"
                      style={{ width: `${(multiProgress.current / multiProgress.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Error message */}
              {error && (
                <p className="text-xs text-red-500 bg-red-50 dark:bg-red-500/10 rounded-xl px-3 py-2">{error}</p>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 pb-4 flex items-center gap-2">
              <button
                onClick={handleSend}
                disabled={!canSend || sending || loading}
                className="h-9 px-4 text-xs font-semibold rounded-xl text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-40 flex items-center gap-1.5 transition-colors"
              >
                {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                {sending ? t('command.sending', 'Sending...') : t('command.send', 'Send')}
              </button>
              <button
                onClick={onClose}
                disabled={sending}
                className="h-9 px-4 text-xs font-semibold rounded-xl text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors"
              >
                {t('settings.cancel')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
